import { useState } from 'react';
import { Button } from '@curtin-dxp/curtin-ui';
import { Check } from 'lucide-react';
import { trpc } from '@/data-loaders';
import { cn } from '@/lib/utils';
import { useAwmsStore } from '@/store';
import { useHandleError, useElementSize } from '@/hooks';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { UnitWorkloadAssignmentProps } from './UnitWorkloadAssignment';
import { AlertModal, ChevronIcon, DeleteButton, Loader } from '@/components/Common';
import { useIsAdmin } from '@/hooks/use-is-admin';

export const UnitWorkloadCampusAssignment = ({
  unitWorkloadDetails,
  unitWorkloadCampuses,
  campusList,
}: UnitWorkloadAssignmentProps) => {
  const { id: unitWorkloadId } = unitWorkloadDetails;
  const handleError = useHandleError();
  const selectedCampusId = useAwmsStore((state) => state.selectedCampusId);
  const activeYear = useAwmsStore((state) => state.activeYear);
  const [triggerRef, { width: triggerWidth }] = useElementSize<HTMLButtonElement>();
  const [open, setOpen] = useState<boolean>(false);
  const trpcUtils = trpc.useUtils();
  const [alert, setAlert] = useState<boolean>(false);
  const [pendingDeleteCampus, setPendingDeleteCampus] = useState<{
    id: number;
    name: string;
    studyModeName: string;
  } | null>(null);
  const { data: isAdmin } = useIsAdmin();

  const handleInvalidateAndRefetch = async (unitWorkloadId: number) => {
    await trpcUtils.getUnitWorkloadCampuses.invalidate({
      unitWorkloadId,
      year: activeYear,
    });
    await trpcUtils.getUnitWorkloadCampuses.refetch();
    await trpcUtils.getAssignedAcademicsForUnitWorkload.invalidate({
      unitWorkloadId,
    });
    await trpcUtils.getAssignedAcademicsForUnitWorkload.refetch();
    if (selectedCampusId > 0) {
      // Scoped invalidate only — an unscoped refetch() hits every cached unit/campus pair. INC0592322
      await trpcUtils.getTeachingDeliveryDetails.invalidate({ unitWorkloadId, campusId: selectedCampusId });
    }
  };

  const { isLoading: isAdding, mutate: addCampusAssignmentMutation } = trpc.addUnitWorkloadCampus.useMutation({
    onSuccess: async () => {
      await handleInvalidateAndRefetch(unitWorkloadId);
    },
    onError: (error) => {
      console.error('Error adding campus:', error);
      handleError([error]);
    },
  });

  const { isLoading: isDeleting, mutate: removeCampusAssignmentMutation } = trpc.removeUnitWorkloadCampus.useMutation({
    onSuccess: async () => {
      await handleInvalidateAndRefetch(unitWorkloadId);
    },
    onError: (error) => {
      console.error('Error removing campus:', error);
      handleError([error]);
    },
  });

  const isMutationLoading = isAdding || isDeleting;

  const addCampusAssignment = (id: number): void => {
    addCampusAssignmentMutation({
      unitWorkloadId: unitWorkloadId,
      campusId: id,
      year: activeYear,
    });
    setOpen(false);
  };

  const removeCampusAssignment = ({ id }: { id: number }): void => {
    removeCampusAssignmentMutation({
      unitWorkloadId: unitWorkloadId,
      campusId: id,
      year: activeYear,
    });
  };

  const isCampusIdExistsInAssignedList = (campusId: number) =>
    unitWorkloadCampuses?.some((unitWorkloadCampuse) => unitWorkloadCampuse.campusId === campusId);

  return (
    <div>
      {isMutationLoading && <Loader />}
      <AlertModal
        title="Do you want to delete the campus?"
        description="This will permanently delete the campus and associated data."
        confirmAction={() => {
          setAlert(false);
          if (pendingDeleteCampus) {
            removeCampusAssignment(pendingDeleteCampus);
          }
        }}
        open={alert}
        cancelAction={() => {
          setAlert(false);
        }}
      />

      <div className="flex max-w-[550px] basis-2/5 flex-col items-center rounded-2xl border border-secondary-grey-04 pb-4 pt-4">
        <div className="flex w-full flex-col justify-start gap-5 px-5 pt-2">
          <div className="flex flex-col justify-start text-lg font-bold">Campus list</div>
          {isAdmin && (
            <div>
              <DropdownMenu open={open}>
                <DropdownMenuTrigger ref={triggerRef} className="focus-visible:outline-none" tabIndex={-1}>
                  <Button
                    className={`h-10 w-[360px] justify-between rounded-lg border-[1px] border-input bg-white px-3 py-5 text-base font-normal leading-6
                    text-gray-600 hover:border-deep-blue focus-visible:ring-1 focus-visible:ring-deep-blue
                    ${open ? 'ring-1 ring-deep-blue' : ''}`}
                    aria-label={'Add campus'}
                    aria-haspopup="menu"
                    onClick={() => {
                      setOpen(!open);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setOpen(!open);
                      }
                    }}
                  >
                    Add campus {''}
                    <ChevronIcon
                      className="h-4 w-4 fill-[#000000] transition-transform duration-300"
                      style={{
                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="scrollbar-w-4 scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar scrollbar-track-[#303030]
                    scrollbar-thumb-[#717171] z-[300] max-h-[calc(40vh)] overflow-y-auto rounded-2xl border-none bg-white shadow-xl"
                  style={{ width: `${triggerWidth}px` }}
                  onInteractOutside={() => setOpen(false)}
                  onEscapeKeyDown={() => setOpen(false)}
                >
                  {campusList &&
                    campusList.map(({ id, name, studyModeName }) => (
                      <DropdownMenuItem
                        key={id}
                        className="py-[10px] text-base font-normal hover:text-deep-blue focus:text-deep-blue"
                        onClick={() => addCampusAssignment(id)}
                        disabled={isCampusIdExistsInAssignedList(id)}
                      >
                        {name} - {studyModeName}
                        <Check
                          className={cn('ml-auto', isCampusIdExistsInAssignedList(id) ? 'opacity-100' : 'opacity-0')}
                        />
                      </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
          <div className="flex w-full flex-col px-4 py-2">
            {unitWorkloadCampuses &&
              unitWorkloadCampuses.map(({ campusId: id, campusName: name, campusStudyMode: studyModeName }) => (
                <div
                  className="flex h-14 flex-row items-center justify-between border-b border-secondary-grey-04"
                  key={id}
                >
                  <div className="flex">
                    {name} - {studyModeName}
                  </div>
                  {unitWorkloadCampuses.length > 1 && isAdmin && (
                    <DeleteButton
                      label="Remove campus"
                      onClick={() => {
                        setPendingDeleteCampus({ id, name, studyModeName });
                        setAlert(true);
                      }}
                    />
                  )}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};
